// MAIN-879 (Fix All): settle many import problems in one place instead of
// opening the editor on every item. It works only on the staged proposals,
// through the same reviewMenuImportProposal Edit/Reject actions as the per-item
// Fix button — it never accepts, publishes or touches a live Menu item, and
// opening it changes nothing.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { REVIEW_MENU_IMPORT_PROPOSAL } from "../../Graphql/mutations";
import { toggleSnackbar } from "../../ReactiveVariables/index";
import {
  describeCardCategory,
  getEffectiveProposalFields,
  getPublishProblems,
} from "./menuImportReview.utils";
import {
  buildBulkFixMessage,
  buildInitialDraft,
  buildSaveRequest,
  getFixProgress,
  getFixableProposals,
  getHeldProposals,
  getRowStatus,
  getUnsavedIds,
  isDuplicateConflict,
  suggestCategory,
  validateDraft,
} from "./menuImportFixAll.utils";

const formatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const DONE = new Set(["fixed", "skipped", "missing"]);

function errorText(error) {
  return error?.graphQLErrors?.[0]?.message || error?.message || "Something went wrong";
}

function FixRow({
  proposal,
  draft,
  fieldsToFix,
  duplicate,
  suggestion,
  categories,
  status,
  selected,
  current,
  disabled,
  onSelect,
  onChange,
  onFocusRow,
  rowRef,
}) {
  const fields = getEffectiveProposalFields(proposal);
  const errors = validateDraft(draft);
  const remaining = getPublishProblems(proposal).map((problem) => problem.message);
  const done = DONE.has(status.key);
  const skipping = draft?.choice === "skip";
  const categoryTitle = (id) =>
    categories.find((category) => String(category._id) === String(id))?.categoryTitle || id;

  return (
    <div
      ref={rowRef}
      className={`menu-fixall-row${current ? " current" : ""}${done ? " done" : ""}`}
      onFocusCapture={onFocusRow}>
      <div className="menu-fixall-row-head">
        <Checkbox
          size="small"
          checked={selected}
          disabled={done || disabled}
          onChange={(e) => onSelect(e.target.checked)}
          inputProps={{ "aria-label": `Select ${fields.itemName || "item"}` }}
        />
        <div className="menu-fixall-row-summary">
          <b>{fields.itemName || "(no name extracted)"}</b>
          <div className="menu-review-meta">
            {describeCardCategory(proposal, categories)} ·{" "}
            {fields.price != null ? formatter.format(fields.price) : "No price"}
            {fields.minQuantity != null ? ` · Min ${fields.minQuantity}` : ""}
          </div>
        </div>
        <span className={`menu-fixall-status menu-fixall-status--${status.key}`}>
          {status.label}
        </span>
      </div>

      {!done && (
        <>
          {remaining.length > 0 && (
            <div className="menu-fixall-issue">Issue: {remaining.join(" · ")}</div>
          )}
          {duplicate && proposal.matchedMenuItem && (
            <div className="menu-review-note">
              Matches your existing <b>{proposal.matchedMenuItem.itemName}</b>
              {proposal.matchedMenuItem.categoryDtls?.categoryTitle
                ? ` · ${proposal.matchedMenuItem.categoryDtls.categoryTitle}`
                : ""}
              {proposal.matchedMenuItem.price != null
                ? ` · ${formatter.format(proposal.matchedMenuItem.price)}`
                : ""}
            </div>
          )}
          {duplicate && (
            <RadioGroup
              row
              value={draft.choice}
              onChange={(e) => onChange({ choice: e.target.value })}>
              <FormControlLabel
                value="fix"
                disabled={disabled}
                control={<Radio size="small" />}
                label="Keep both — give this one a different name or category"
              />
              <FormControlLabel
                value="skip"
                disabled={disabled}
                control={<Radio size="small" />}
                label="Skip — don't import this item"
              />
            </RadioGroup>
          )}
          {suggestion && !skipping && (
            <div className="menu-review-suggested">
              {suggestion.reason}
              {!suggestion.categoryId && suggestion.candidates.length > 1 && (
                <span className="menu-fixall-candidates">
                  {suggestion.candidates.map((id) => (
                    <Button
                      key={id}
                      size="small"
                      variant={String(draft.categoryId) === String(id) ? "contained" : "outlined"}
                      disabled={disabled}
                      onClick={() => onChange({ categoryId: id, suggestedCategory: false })}>
                      {categoryTitle(id)}
                    </Button>
                  ))}
                </span>
              )}
            </div>
          )}
          {!skipping && (
            <div className="menu-fixall-fields">
              {fieldsToFix.includes("itemName") && (
                <TextField
                  size="small"
                  label="Item name"
                  value={draft.itemName}
                  disabled={disabled}
                  onChange={(e) => onChange({ itemName: e.target.value })}
                />
              )}
              {fieldsToFix.includes("categoryId") && (
                <FormControl
                  size="small"
                  className="menu-fixall-category"
                  // MUI's own min-width: 0 wins over a class, so the width is set here.
                  sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                  <InputLabel id={`fixall-cat-${proposal._id}`}>Category</InputLabel>
                  <Select
                    labelId={`fixall-cat-${proposal._id}`}
                    label="Category"
                    value={draft.categoryId || ""}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange({ categoryId: e.target.value, suggestedCategory: false })
                    }>
                    {categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.categoryTitle}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {fieldsToFix.includes("price") && (
                <TextField
                  size="small"
                  label="Price"
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  value={draft.price}
                  disabled={disabled}
                  error={Boolean(errors.price)}
                  helperText={errors.price}
                  onChange={(e) => onChange({ price: e.target.value })}
                />
              )}
              {fieldsToFix.includes("minQuantity") && (
                <TextField
                  size="small"
                  label="Minimum quantity"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={draft.minQuantity}
                  disabled={disabled}
                  error={Boolean(errors.minQuantity)}
                  helperText={errors.minQuantity}
                  onChange={(e) => onChange({ minQuantity: e.target.value })}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function MenuImportFixAllDialog({ proposals, categories = [], onClose, onSaved }) {
  const fullScreen = useMediaQuery("(max-width:600px)");
  const [reviewProposal] = useMutation(REVIEW_MENU_IMPORT_PROPOSAL);

  // The items the panel opened with stay listed — fixed ones read "Fixed" —
  // so progress means something and nothing jumps around while working.
  const [opened] = useState(() =>
    getFixableProposals(proposals).map((proposal) => ({
      id: String(proposal._id),
      duplicate: isDuplicateConflict(proposal),
      fieldsToFix: [
        ...new Set([
          ...getPublishProblems(proposal).map((problem) => problem.field),
          // A duplicate can be kept by renaming it or by filing it elsewhere.
          ...(isDuplicateConflict(proposal) ? ["itemName", "categoryId"] : []),
        ]),
      ].filter(Boolean),
      suggestion: suggestCategory(proposal, categories),
    })),
  );
  const openedIds = useMemo(() => opened.map((row) => row.id), [opened]);
  const [drafts, setDrafts] = useState(() => {
    const initial = {};
    getFixableProposals(proposals).forEach((proposal) => {
      initial[String(proposal._id)] = buildInitialDraft(proposal, categories);
    });
    return initial;
  });
  // A reliable suggestion is a prepared fix, so it starts selected; the
  // caterer can untick it. Nothing is saved until they apply.
  const [selected, setSelected] = useState(
    () => new Set(Object.keys(drafts).filter((id) => drafts[id].suggestedCategory)),
  );
  const [saveErrors, setSaveErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [result, setResult] = useState(null);
  const [bulkCategory, setBulkCategory] = useState("");
  const [currentId, setCurrentId] = useState(openedIds[0] || null);
  const rowRefs = useRef({});
  // Items the caterer changed here. Leaving with any of them unsaved asks
  // first; what the panel prepared by itself never does.
  const [touched, setTouched] = useState(() => new Set());
  // null, or why leaving was asked for: "close" (Close, Escape, backdrop) or
  // "back" (the browser's Back button).
  const [leaveRequest, setLeaveRequest] = useState(null);
  // Bumped to re-arm the Back guard after "Continue editing".
  const [guardVersion, setGuardVersion] = useState(0);

  const byId = useMemo(() => new Map(proposals.map((p) => [String(p._id), p])), [proposals]);
  const progress = getFixProgress(openedIds, proposals);
  const heldCount = getHeldProposals(proposals).length;
  const statusOf = (id) => getRowStatus(byId.get(id), drafts[id], saveErrors[id]);
  const openRows = openedIds.filter((id) => !DONE.has(statusOf(id).key));
  const selectedOpen = [...selected].filter((id) => openRows.includes(id));
  const selectedNeedingCategory = selectedOpen.filter((id) =>
    opened.find((row) => row.id === id)?.fieldsToFix.includes("categoryId"),
  );
  const unsavedCount = getUnsavedIds({ touched, drafts, proposals, saveErrors }).length;
  const hasUnsaved = unsavedCount > 0;

  // Reloading, closing the tab or leaving the site: browsers only allow
  // their own prompt here, not a custom dialog.
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsaved]);

  // The browser's Back button. The app uses a plain BrowserRouter, which
  // cannot block navigation, so while there are unsaved changes a copy of the
  // current history entry sits on top: Back pops only that copy — same page,
  // panel still open — and the panel asks before really leaving.
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    window.history.pushState(window.history.state, "", window.location.href);
    let guarded = true;
    const onPopState = () => {
      if (!guarded) return;
      guarded = false;
      setLeaveRequest("back");
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      // Nothing left to protect: take the copy off again so Back works normally.
      if (guarded) window.history.back();
    };
  }, [hasUnsaved, guardVersion]);

  function markTouched(ids) {
    setTouched((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }

  /** Close, Escape or a backdrop click: ask first only if something is unsaved. */
  function requestClose() {
    if (savingRef.current) return;
    if (hasUnsaved) setLeaveRequest("close");
    else onClose();
  }

  function continueEditing() {
    if (leaveRequest === "back") setGuardVersion((v) => v + 1);
    setLeaveRequest(null);
  }

  function discardChanges() {
    const wasBack = leaveRequest === "back";
    setLeaveRequest(null);
    onClose();
    // Finish the Back the caterer asked for; its guard entry is already gone.
    if (wasBack) window.history.back();
  }

  function updateDraft(id, patch) {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    setSelected((prev) => new Set(prev).add(id));
    markTouched([id]);
    setSaveErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function setRowSelected(id, on) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function applyBulkCategory() {
    if (!bulkCategory) return;
    setDrafts((prev) => {
      const next = { ...prev };
      selectedNeedingCategory.forEach((id) => {
        next[id] = { ...next[id], categoryId: bulkCategory, suggestedCategory: false };
      });
      return next;
    });
    markTouched(selectedNeedingCategory);
  }

  function goTo(step) {
    const order = openedIds;
    const start = Math.max(order.indexOf(currentId), 0);
    for (let i = 1; i <= order.length; i += 1) {
      const id = order[(start + step * i + order.length * i) % order.length];
      if (!DONE.has(statusOf(id).key)) {
        setCurrentId(id);
        rowRefs.current[id]?.scrollIntoView?.({ block: "center", behavior: "smooth" });
        return;
      }
    }
  }

  /**
   * Save the given items one at a time, keep every success, and report what
   * the refreshed items actually say. A second click while saving is ignored.
   */
  async function save(ids) {
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    setResult(null);
    const errors = {};
    const savedIds = [];
    try {
      for (const id of ids) {
        const proposal = byId.get(id);
        const draft = drafts[id];
        if (!proposal || DONE.has(statusOf(id).key)) continue;
        if (Object.keys(validateDraft(draft)).length) {
          errors[id] = "check the values entered";
          continue;
        }
        const request = buildSaveRequest(proposal, draft);
        if (!request) continue;
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await reviewProposal({ variables: request });
          if (res?.errors?.length) throw res.errors[0];
          savedIds.push(id);
        } catch (err) {
          errors[id] = errorText(err);
        }
      }
      const fresh = (await onSaved())?.data?.listMenuImportProposals || proposals;
      const freshById = new Map(fresh.map((p) => [String(p._id), p]));
      setSaveErrors((prev) => {
        const next = { ...prev, ...errors };
        savedIds.forEach((id) => delete next[id]);
        return next;
      });
      setDrafts((prev) => {
        const next = { ...prev };
        savedIds.forEach((id) => {
          const p = freshById.get(id);
          if (p) next[id] = { ...buildInitialDraft(p, categories), choice: prev[id].choice };
        });
        return next;
      });
      setSelected((prev) => {
        const next = new Set(prev);
        savedIds.forEach((id) => next.delete(id));
        return next;
      });
      // Saved changes are no longer at risk; failed ones still are.
      setTouched((prev) => {
        const next = new Set(prev);
        savedIds.forEach((id) => next.delete(id));
        return next;
      });
      const after = getFixProgress(openedIds, fresh);
      const outcome = buildBulkFixMessage({
        saved: savedIds.length,
        failed: Object.keys(errors).length,
        remaining: after.remaining,
      });
      setResult(outcome);
      toggleSnackbar({ status: true, message: outcome.message, variant: outcome.variant });
      return Object.keys(errors).length === 0;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  async function saveAndNext() {
    if (!currentId) return;
    const ok = await save([currentId]);
    if (ok) goTo(1);
  }

  const missingRows = opened.filter((row) => !row.duplicate);
  const duplicateRows = opened.filter((row) => row.duplicate);
  const renderRow = (row) => {
    const proposal = byId.get(row.id);
    if (!proposal) return null;
    return (
      <FixRow
        key={row.id}
        rowRef={(node) => {
          rowRefs.current[row.id] = node;
        }}
        proposal={proposal}
        draft={drafts[row.id]}
        fieldsToFix={row.fieldsToFix}
        duplicate={row.duplicate}
        suggestion={getEffectiveProposalFields(proposal).categoryId ? null : row.suggestion}
        categories={categories}
        status={statusOf(row.id)}
        selected={selected.has(row.id)}
        current={currentId === row.id}
        disabled={saving}
        onSelect={(on) => setRowSelected(row.id, on)}
        onChange={(patch) => updateDraft(row.id, patch)}
        onFocusRow={() => setCurrentId(row.id)}
      />
    );
  };
  const currentReady =
    currentId &&
    !DONE.has(statusOf(currentId).key) &&
    buildSaveRequest(byId.get(currentId), drafts[currentId]);

  return (
    <>
      <Dialog open onClose={requestClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
        <DialogTitle>Fix Import Issues</DialogTitle>
        <DialogContent dividers className="menu-fixall">
          <div className="menu-fixall-progress" aria-live="polite">
            <b>
              {progress.resolved} of {progress.total} issues resolved
            </b>
            <LinearProgress
              variant="determinate"
              value={progress.total ? (progress.resolved / progress.total) * 100 : 0}
              aria-label="Issues resolved"
            />
            <div className="menu-review-hint">
              Nothing is accepted or published from here — fixed items keep their review status.
              {heldCount
                ? ` ${heldCount} ${heldCount === 1 ? "item is" : "items are"} held by open orders and can't be changed.`
                : ""}
            </div>
          </div>

          {result && (
            <div
              className={`menu-fixall-result menu-fixall-result--${result.variant}`}
              role="status">
              {result.message}
            </div>
          )}

          <div className="menu-fixall-toolbar">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={openRows.length > 0 && selectedOpen.length === openRows.length}
                  indeterminate={selectedOpen.length > 0 && selectedOpen.length < openRows.length}
                  disabled={!openRows.length || saving}
                  onChange={(e) => setSelected(new Set(e.target.checked ? openRows : []))}
                />
              }
              label={`${selectedOpen.length} selected`}
            />
            <FormControl
              size="small"
              className="menu-fixall-category"
              // MUI's own min-width: 0 wins over a class, so the width is set here.
              sx={{ minWidth: { xs: "100%", sm: 220 } }}>
              <InputLabel id="fixall-bulk-category">Category for selected</InputLabel>
              <Select
                labelId="fixall-bulk-category"
                label="Category for selected"
                value={bulkCategory}
                disabled={saving}
                onChange={(e) => setBulkCategory(e.target.value)}>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.categoryTitle}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              disabled={!bulkCategory || !selectedNeedingCategory.length || saving}
              onClick={applyBulkCategory}>
              Set for {selectedNeedingCategory.length} without a category
            </Button>
          </div>

          {missingRows.length > 0 && (
            <section>
              <h5 className="menu-fixall-section">
                Missing or invalid details ({missingRows.length})
              </h5>
              {missingRows.map(renderRow)}
            </section>
          )}
          {duplicateRows.length > 0 && (
            <section>
              <h5 className="menu-fixall-section">Possible duplicates ({duplicateRows.length})</h5>
              <div className="menu-review-hint">
                Your existing menu items are never changed or removed from here.
              </div>
              {duplicateRows.map(renderRow)}
            </section>
          )}
          {!opened.length && <p>There's nothing to fix right now.</p>}
        </DialogContent>
        <DialogActions className="menu-fixall-actions">
          <Button onClick={() => goTo(-1)} disabled={!openRows.length || saving}>
            Previous
          </Button>
          <Button onClick={() => goTo(1)} disabled={!openRows.length || saving}>
            Next
          </Button>
          <Button variant="outlined" onClick={saveAndNext} disabled={!currentReady || saving}>
            Save &amp; Next
          </Button>
          <span style={{ flex: 1 }} />
          <Button onClick={requestClose} disabled={saving}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => save(selectedOpen)}
            disabled={!selectedOpen.length || saving}>
            {saving
              ? "Saving…"
              : `Apply ${selectedOpen.length || ""} selected fix${selectedOpen.length === 1 ? "" : "es"}`}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(leaveRequest)}
        onClose={continueEditing}
        maxWidth="xs"
        aria-labelledby="fixall-unsaved-title">
        <DialogTitle id="fixall-unsaved-title">Discard unsaved changes?</DialogTitle>
        <DialogContent>
          You've changed {unsavedCount} {unsavedCount === 1 ? "item" : "items"} without saving. If
          you leave now, {unsavedCount === 1 ? "that change" : "those changes"} will be lost.
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={discardChanges}>
            Discard Changes
          </Button>
          <Button variant="contained" onClick={continueEditing} autoFocus>
            Continue Editing
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
